<template>
  <div class="feedback-list-container">
    <div class="list-content">
      <KeyFeedbackCard
        v-for="(item, index) in insightFormList"
        :key="index"
        :item="item"
        :can-manage="canManageFeedback(item)"
        :show-author="showFeedbackAuthor"
        :default-author-name="visitOwnerUserName"
        :show-ai-optimized-content="showAiOptimizedContent"
        @delete="onDelete(index)"
        @change-topic="editingIndex = index; showTopicPopup = true"
        @update="handleUpdate($event, index)"
      />

      <div class="bottom-bar">
        <div v-if="suggestion" class="suggestion">
          <font-awesome-icon :icon="['far', 'lightbulb']" style="color: #aa341b; font-size: 16px; margin-top: 2px; margin-right: 8px;" />
          <span>{{ suggestion }}</span>
        </div>
        <div class="input-area">
          <div class="input-row">
            <div class="add-question-btn" @click="showTopicPopup = true">
              <van-icon name="plus" /> <span>{{ $t('question_label') }}</span>
            </div>
            <div class="input-wrapper">
              <div v-if="currentQuestion" class="selected-question-bar">
                <div class="question-info">
                  <div class="text">{{ currentQuestion.text }}</div>
                  <div v-if="currentQuestion.description" class="desc">{{ currentQuestion.description }}</div>
                </div>
                <van-icon name="cross" class="close-btn" @click="clearCurrentQuestion" />
              </div>

              <div v-if="currentQuestion && currentQuestion.question_type === 'single'" class="single-choice-box">
                <div
                  v-for="(opt, idx) in currentQuestion.parsedOptions"
                  :key="idx"
                  class="option-item"
                  :class="{ 'active': inputText === opt.value }"
                  @click="inputText = opt.value"
                >
                  {{ opt.label }}
                </div>
              </div>
              <div v-else-if="pendingVoiceData" class="pending-voice-bar">
                <AudioPlayer
                  :local-id="pendingVoiceData.localId"
                  :duration="pendingVoiceData.duration + '&quot;'"
                  :show-delete="true"
                  style="width: 200px"
                  @delete="pendingVoiceData = null"
                />
              </div>
              <van-field
                v-else
                v-model="inputText"
                type="textarea"
                autosize
                rows="1"
                :border="false"
                class="text-input"
                :placeholder="$t('input_feedback_placeholder')"
              />
            </div>
            <div class="action-btn">
              <van-icon
                v-if="inputText || pendingVoiceData"
                name="guide-o"
                size="28"
                color="#aa341b"
                style="padding: 4px;"
                @click="sendText"
              />
              <SpeechToText
                v-else-if="!currentQuestion || currentQuestion.question_type !== 'single'"
                :sub-title="$t('speech_subtitle')"
                @recordComplete="handleRecordComplete"
                @translateResult="handleTranslateResult"
              >
                <template #trigger>
                  <div class="mic-btn">
                    <font-awesome-icon icon="microphone" style="color: #fff; font-size: 28px;" />
                  </div>
                </template>
              </SpeechToText>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Topic Selector Popup -->
    <van-popup
      v-model="showTopicPopup"
      position="bottom"
      :style="{ height: '80%' }"
    >
      <div class="popup-header">
        <div class="title">{{ $t('select_key_topic_title') }}</div>
        <van-icon name="close" size="24" @click="showTopicPopup = false" />
      </div>
      <van-empty v-if="optionsKIT.length === 0" :description="$t('no_key_questions')" />
      <van-tree-select
        v-else
        height="calc(100% - 57px)"
        :items="optionsKIT"
        :main-active-index.sync="activeTopicIndex"
        :active-id.sync="activeQuestionId"
      >
        <template #content>
          <div v-if="optionsKIT[activeTopicIndex] && optionsKIT[activeTopicIndex].children" class="custom-tree-content">
            <div
              v-for="child in optionsKIT[activeTopicIndex].children"
              :key="child.id"
              class="custom-tree-item"
              :class="{ 'active': activeQuestionId === child.id }"
              @click="onSelectQuestion(child)"
            >
              <div class="q-title">
                <span class="q-text">{{ child.text }}</span>
                <span v-if="child.question_type" class="q-type">{{ getQuestionTypeLabel(child.question_type) }}</span>
              </div>
              <div v-if="child.description" class="q-desc">{{ child.description }}</div>
            </div>
          </div>
        </template>
      </van-tree-select>
    </van-popup>
  </div>
</template>

<script>
import KeyFeedbackCard from './KeyFeedbackCard.vue';
import AudioPlayer from './AudioPlayer.vue';
import SpeechToText from '@/components/SpeechToText.vue';
import {
  createEditInsightRecord,
  fetchPhysicianMslInsights,
  fetchInsightTopics,
  createEditPhysicianMslInsightQuestions,
  deletePhysicianMslInsightQuestions,
  createPhysicianMrCallKeyFeedback,
  updatePhysicianMrCallKeyFeedback,
  deletePhysicianMrCallKeyFeedback,
  fetchPhysicianMrCallKeyFeedbacks,
  syncVoiceToS3,
  fetchKeyTopicsNew,
  fetchPhysicianMslCalls
} from '@/api/kol';
import { getPhysiciansDetail } from '@/api/Physicians';
import { streamFeedbackSummary } from '@/api/ai';
import {getLocalStorage} from '@/utils';
import {getVisitFeedbackDisplayConfig} from '@/utils/visitFeedbackDisplay';
import {wecomSDK} from '@/utils/wecom';
import {Toast, Dialog, Empty} from 'vant';

export default {
  components: {KeyFeedbackCard, SpeechToText, AudioPlayer, [Empty.name]: Empty},
  filters: {
    truncate(value, length) {
      if (!value) return '';
      if (value.length <= length) return value;
      return value.substring(0, length) + '...';
    },
  },
  props: {
    physician_id: {
      type: String,
      default: '',
    },
    source_msl_call_id: {
      type: String,
      default: '',
    },
    visitOwnerUserId: {
      type: String,
      default: '',
    },
    visitOwnerUserName: {
      type: String,
      default: '',
    },
    showFeedbackAuthor: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      insightFormList: [],
      insightTopicList: [],
      optionsKIT: [],
      inputText: '',
      showTopicPopup: false,
      activeTopicIndex: 0,
      activeQuestionId: null,
      currentQuestion: null,
      suggestion: this.$t('default_suggestion'),
      tempTranslateResult: '',
      pendingVoiceData: null,
      autoSendingVoice: false,
      editingIndex: null,
      deletedIds: [],
      originalSnapshot: '[]',
      baselineDraftState: '{}',
    };
  },
  computed: {
    showAiOptimizedContent() {
      return getVisitFeedbackDisplayConfig(
        getLocalStorage('role_name'),
      ).showAiOptimizedContent;
    },
  },
  watch: {
    source_msl_call_id: {
      handler(value) {
        if (value) {
          this.fetchInsightList({source_msl_call_id: value});
        }
      },
      immediate: true,
    },
    insightFormList: {
      handler() {
        this.emitDraftChange();
      },
      deep: true,
    },
    deletedIds: {
      handler() {
        this.emitDraftChange();
      },
      deep: true,
    },
    inputText() {
      this.emitDraftChange();
    },
    currentQuestion: {
      handler() {
        this.emitDraftChange();
      },
      deep: true,
    },
    suggestion() {
      this.emitDraftChange();
    },
    pendingVoiceData: {
      handler() {
        this.emitDraftChange();
      },
      deep: true,
    },
    activeTopicIndex() {
      this.emitDraftChange();
    },
    activeQuestionId() {
      this.emitDraftChange();
    },
    tempTranslateResult() {
      this.emitDraftChange();
    },
  },
  mounted() {
    this.resetDraftBaseline();
    this.fetchInsightTopics();
  },
  methods: {
    currentUserId() {
      return String(getLocalStorage('user_id') || '');
    },
    isCurrentUserVisitOwner() {
      const ownerUserId = String(this.visitOwnerUserId || '');
      return Boolean(ownerUserId) && ownerUserId === this.currentUserId();
    },
    getCurrentFeedbackAuthorFields() {
      if (this.isCurrentUserVisitOwner()) return {};

      const userId = this.currentUserId();
      const userName = String(getLocalStorage('user_name') || '');
      return userId
        ? {
          user_id: userId,
          user_name: userName,
        }
        : {};
    },
    canManageFeedback(item = {}) {
      const feedbackUserId = String(item.user_id || '');
      if (feedbackUserId) {
        return feedbackUserId === this.currentUserId();
      }
      return this.isCurrentUserVisitOwner();
    },
    isEnglishLocale() {
      return this.$i18n && /^en/i.test(this.$i18n.locale);
    },
    escapeRegExp(value) {
      return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    getAiSectionTitleMap() {
      if (this.isEnglishLocale()) {
        return {
          summary: 'Compliant Visit Feedback Summary',
          tags: 'Keywords',
          suggestions: 'Next Follow-up Suggestions'
        };
      }

      return {
        summary: '一｜合规后的拜访反馈总结',
        tags: '二｜关键词',
        suggestions: '三｜下一步追问建议'
      };
    },
    extractAiSection(text, currentHeadings, nextHeadings) {
      const currentPattern = currentHeadings.map(this.escapeRegExp).join('|');
      const nextPattern = nextHeadings.length ? nextHeadings.map(this.escapeRegExp).join('|') : null;
      const sectionRegex = new RegExp(
        nextPattern
          ? `【\\s*(?:\\d+\\s*[|｜]\\s*)?(?:${currentPattern})\\s*】([\\s\\S]*?)(?=【\\s*(?:\\d+\\s*[|｜]\\s*)?(?:${nextPattern})\\s*】|$)`
          : `【\\s*(?:\\d+\\s*[|｜]\\s*)?(?:${currentPattern})\\s*】([\\s\\S]*?)$`,
        'i'
      );
      const match = text.match(sectionRegex);
      return match ? match[1].trim() : '';
    },
    buildFeedbackPrompt(rawContent, contextInfo) {
      const sectionTitles = this.getAiSectionTitleMap();

      if (this.isEnglishLocale()) {
        return `Please summarize the following physician feedback in English and keep the exact structure below:\n【${sectionTitles.summary}】\n...\n【${sectionTitles.tags}】\n...\n【${sectionTitles.suggestions}】\n...\n\n1. Voice transcript (may include spoken language, repetition, or non-linear logic): ${rawContent}\n2. Key topic and key question selected by the representative: ${this.currentQuestion ? (this.insightTopicList[this.activeTopicIndex]?.topic_name + ' - ' + this.currentQuestion.text) : 'Not specified'}\n3. Physician ID and visit context: ${contextInfo}`;
      }

      return `请用中文总结以下医生拜访反馈，并严格按下面结构输出：\n【${sectionTitles.summary}】\n...\n【${sectionTitles.tags}】\n...\n【${sectionTitles.suggestions}】\n...\n\n1. 语音转写文本（可能存在口语、重复、逻辑跳跃）：${rawContent}\n2. 代表选择的关键主题、关键问题：${this.currentQuestion ? (this.insightTopicList[this.activeTopicIndex]?.topic_name + ' - ' + this.currentQuestion.text) : '未指定'}\n3. physician_id及拜访上下文：${contextInfo}`;
    },
    emitDraftChange() {
      this.$emit('draft-change', this.getDraftState());
    },
    getDraftState() {
      return {
        insightFormList: JSON.parse(JSON.stringify(this.insightFormList || [])),
        deletedIds: JSON.parse(JSON.stringify(this.deletedIds || [])),
        inputText: this.inputText || '',
        currentQuestion: this.currentQuestion
          ? JSON.parse(JSON.stringify(this.currentQuestion))
          : null,
        suggestion: this.suggestion || '',
        tempTranslateResult: this.tempTranslateResult || '',
        pendingVoiceData: this.pendingVoiceData
          ? JSON.parse(JSON.stringify(this.pendingVoiceData))
          : null,
        activeTopicIndex: this.activeTopicIndex,
        activeQuestionId: this.activeQuestionId,
      };
    },
    resetDraftBaseline() {
      this.baselineDraftState = JSON.stringify(this.getDraftState());
    },
    applyDraftState(draftState = {}) {
      this.insightFormList = JSON.parse(JSON.stringify(draftState.insightFormList || []));
      this.deletedIds = JSON.parse(JSON.stringify(draftState.deletedIds || []));
      this.inputText = draftState.inputText || '';
      this.currentQuestion = draftState.currentQuestion
        ? JSON.parse(JSON.stringify(draftState.currentQuestion))
        : null;
      this.suggestion = draftState.suggestion || this.$t('default_suggestion');
      this.tempTranslateResult = draftState.tempTranslateResult || '';
      this.pendingVoiceData = draftState.pendingVoiceData
        ? JSON.parse(JSON.stringify(draftState.pendingVoiceData))
        : null;
      this.activeTopicIndex = draftState.activeTopicIndex || 0;
      this.activeQuestionId = draftState.activeQuestionId || null;
      this.mapTopicsToFeedback();
      this.originalSnapshot = JSON.stringify(this.insightFormList);
      this.resetDraftBaseline();
    },
    hasUnsavedChanges() {
      return this.baselineDraftState !== JSON.stringify(this.getDraftState());
    },
    hasDraftableChanges() {
      return Boolean(
        (this.inputText && this.inputText.trim()) ||
        this.pendingVoiceData ||
        this.currentQuestion ||
        this.tempTranslateResult,
      );
    },
    async fetchInsightTopics() {
      // Use new API for KeyTopics/KeyQuestions
      const role_id = getLocalStorage('role_id');
      const res = await fetchKeyTopicsNew(role_id);
      this.insightTopicList = res;
      this.optionsKIT = this.insightTopicList.map((item) => ({
        text: item.topic_name || item.name,
        id: item.key_topic_id || item.insight_topic_id,
        children: (item.key_topic__key_questions || item.insight_topic__insight_questions || []).map((q) => ({
          text: q.question || q.name,
          id: q.key_question_id || q.insight_question_id,
          ...q,
        })),
      }));
      this.mapTopicsToFeedback();
    },
    mapTopicsToFeedback() {
      if (this.insightFormList.length > 0 && this.insightTopicList.length > 0) {
        this.insightFormList = this.insightFormList.map(item => {
          if (item.key_question_id) {
            for (const t of this.insightTopicList) {
              const questions = t.key_topic__key_questions || t.insight_topic__insight_questions || [];
              const q = questions.find(iq => (iq.key_question_id || iq.insight_question_id) === item.key_question_id);
              if (q) {
                item.topic_name = item.topic_name || t.topic_name || t.name;
                item.question = item.question || q.question || q.name;
                item.description = item.description || q.description;
                item.question_type = item.question_type || q.question_type;
                break;
              }
            }
          }
          return item;
        });
      }
    },
    async fetchInsightList({source_msl_call_id, physician_id}) {
      if (!source_msl_call_id) return;

      // Reset suggestion to default
      this.suggestion = this.$t('default_suggestion');

      const res = await fetchPhysicianMrCallKeyFeedbacks({
        physician_mr_call_id: source_msl_call_id
      });

      this.insightFormList = res.value.map((item) => {
        // Find topic/question name from metadata list if loaded
        let topic_name = '';
        let question = '';

        if (item.key_question) {
          question = item.key_question.question;
          item.description = item.key_question.description;
          item.question_type = item.key_question.question_type;
        }

        return {
          ...item,
          topic_name,
          question,
          tags: item.feedback_labels ? item.feedback_labels.split(',') : [],
        };
      });

      this.mapTopicsToFeedback();

      this.originalSnapshot = JSON.stringify(this.insightFormList);

      // Update suggestion from the last feedback item
      if (this.insightFormList.length > 0) {
        const lastItem = this.insightFormList[this.insightFormList.length - 1];
        if (lastItem.feedback_summary) {
          const parsed = this.parseAIResponse(lastItem.feedback_summary);
          if (parsed.suggestions && parsed.suggestions.length > 0) {
            this.suggestion = this.$t('suggestion_prefix') + parsed.suggestions[0];
          }
        }
      }
      this.resetDraftBaseline();
      this.emitDraftChange();
    },
    getQuestionTypeLabel(type) {
      if (type === 'single') return this.$t('key_feedback_question_type_single');
      if (type === 'multiple') return this.$t('key_feedback_question_type_multiple');
      if (type === 'freetext') return this.$t('key_feedback_question_type_voice');
      return type;
    },
    onSelectQuestion(data) {
      let parsedOptions = [];
      if (data.options) {
        try {
          parsedOptions = typeof data.options === 'string' ? JSON.parse(data.options) : data.options;
        } catch (e) {
          console.error('Failed to parse options', e);
        }
      }
      const questionData = { ...data, parsedOptions };

      if (this.editingIndex !== null) {
        // Editing existing item (Topic/Question change)
        const item = this.insightFormList[this.editingIndex];
        if (!this.canManageFeedback(item)) {
          this.editingIndex = null;
          this.showTopicPopup = false;
          return;
        }
        const topic = this.insightTopicList[this.activeTopicIndex];

        this.$set(this.insightFormList, this.editingIndex, {
          ...item,
          key_question_id: data.id,
          question: data.text,
          description: data.description, // update description handling
          question_type: data.question_type, // update question_type handling
          topic_name: topic ? topic.topic_name || topic.name : '',
          _isModified: true
          // insight_topic_id: topic ? topic.insight_topic_id : null
        });

        this.editingIndex = null;
        this.showTopicPopup = false;
      } else {
        this.currentQuestion = questionData;
        this.showTopicPopup = false;
        this.inputText = ''; // clear input when selecting a new question
      }
    },
    clearCurrentQuestion() {
      this.currentQuestion = null;
      this.inputText = '';
    },
    handleUpdate(updatedItem, index) {
      if (!this.canManageFeedback(this.insightFormList[index])) return;
      this.$set(this.insightFormList, index, { ...updatedItem, _isModified: true });
    },
    onDelete(index) {
      if (!this.canManageFeedback(this.insightFormList[index])) return;
      Dialog.confirm({
        title: this.$t('prompt_title'),
        message: this.$t('confirm_delete_feedback'),
      })
        .then(() => {
          const item = this.insightFormList[index];
          if (item.physician_mr_call_key_feedback_id) {
            this.deletedIds.push(item.physician_mr_call_key_feedback_id);
          }
          this.insightFormList.splice(index, 1);
        })
        .catch(() => {
          // on cancel
        });
    },
    parseAIResponse(text) {
      let summary = '';
      let tags = [];
      let suggestions = [];
      const sectionTitles = this.getAiSectionTitleMap();
      const summaryHeadings = [sectionTitles.summary, '一｜合规后的拜访反馈总结', 'Compliant Visit Feedback Summary', 'Compliance-adjusted Visit Feedback Summary', 'Compliance adjusted Visit Feedback Summary'];
      const tagsHeadings = [sectionTitles.tags, '二｜关键词', 'Keywords'];
      const suggestionHeadings = [sectionTitles.suggestions, '三｜下一步追问建议', 'Next Follow-up Suggestions', 'Suggested Follow-up Question', 'Suggested Follow-up Questions'];

      // 1. Extract Summary
      summary = this.extractAiSection(text, summaryHeadings, [...tagsHeadings, ...suggestionHeadings]);
      if (!summary) {
        summary = text;
      }

      // 2. Extract Tags
      const rawTags = this.extractAiSection(text, tagsHeadings, suggestionHeadings);
      if (rawTags) {
        if (rawTags) {
          tags = rawTags.split(/[、,\n]/).map(t => t.trim()).filter(t => t && t !== '-');
        }
      }

      // 3. Extract Suggestions
      const rawSugg = this.extractAiSection(text, suggestionHeadings, []);
      if (rawSugg) {
        suggestions = rawSugg.split(/[?？\n]/).map(t => t.trim().replace(/^[-*•]\s*/, '')).filter(t => t);
      }

      return { summary, tags, suggestions };
    },
    async sendText() {
      if (!this.inputText.trim() && !this.pendingVoiceData) return;

      if (this.pendingVoiceData && !this.pendingVoiceData.feedback_voice_media_id) {
        Toast('语音上传中，请稍后');
        return;
      }

      // Determine content: text input takes precedence, otherwise use voice translation
      let rawContent = '';
      let newItem = {};
      const selectedQuestion = this.currentQuestion
        ? JSON.parse(JSON.stringify(this.currentQuestion))
        : null;
      const selectedTopicName = selectedQuestion
        ? (this.insightTopicList[this.activeTopicIndex]?.topic_name || this.insightTopicList[this.activeTopicIndex]?.name || '')
        : '';

      const isDirectChoice = selectedQuestion && (selectedQuestion.question_type === 'single' || selectedQuestion.question_type === 'multiple');

      if (this.pendingVoiceData && !isDirectChoice) {
        // Voice Mode
        if (this.pendingVoiceData.feedback_transcript) {
          rawContent = this.pendingVoiceData.feedback_transcript;
        } else if (this.pendingVoiceData.translateResult) {
          rawContent = this.pendingVoiceData.translateResult;
        }

        newItem = {
          feedback_summary: '', // Will be filled by AI
          feedback_transcript: rawContent,
          feedback_content: '', // Empty for voice, as it's transcript
          key_question_id: selectedQuestion?.id,
          topic_name: selectedTopicName,
          question: selectedQuestion?.text,
          description: selectedQuestion?.description, // Pass description
          question_type: selectedQuestion?.question_type, // Pass type
          tags: [],
          isAiLoading: true,
          ...this.pendingVoiceData
        };
      } else {
        // Text Mode or Direct Choice
        rawContent = this.inputText.trim();
        newItem = {
          feedback_summary: isDirectChoice ? rawContent : '', // For direct choice, summary is just the raw text
          feedback_transcript: '',
          feedback_content: rawContent, // Saved as content
          key_question_id: selectedQuestion?.id,
          topic_name: selectedTopicName,
          question: selectedQuestion?.text,
          description: selectedQuestion?.description, // Pass description
          question_type: selectedQuestion?.question_type, // Pass type
          tags: [],
          isAiLoading: !isDirectChoice // Don't load AI for single/multiple
        };
      }

      newItem = {
        ...newItem,
        ...this.getCurrentFeedbackAuthorFields(),
      };
      this.insightFormList.push(newItem);
      const currentItemIndex = this.insightFormList.length - 1;

      // Clear input immediately
      this.inputText = '';
      this.pendingVoiceData = null;
      this.clearCurrentQuestion();

      if (isDirectChoice) {
        // Skip AI summary for single/multiple choice questions
        return;
      }

      // Get context data
      let contextInfo = `Physician ID: ${this.physician_id || 'Unknown'}`;
      try {
        if (this.physician_id) {
          const [phyRes, callRes] = await Promise.all([
            getPhysiciansDetail(this.physician_id).catch(e => null),
            fetchPhysicianMslCalls({ physician_id: this.physician_id }, 0, 3).catch(e => null)
          ]);

          if (phyRes && phyRes.entity) {
            const p = phyRes.entity;
            contextInfo += this.isEnglishLocale()
              ? `\nPhysician profile: ${p.physician_name} (${p.department_name}, ${p.professional_title}, ${p.institution_name})`
              : `\n医生画像: ${p.physician_name} (${p.department_name}, ${p.professional_title}, ${p.institution_name})`;
          }
          if (callRes && callRes.value && callRes.value.length) {
            const recentCalls = callRes.value.map(c => `- ${c.call_time ? c.call_time.substring(0, 10) : ''}: ${c.call_objective || 'No objective'}`).join('\n');
            contextInfo += this.isEnglishLocale()
              ? `\nRecent visits:\n${recentCalls}`
              : `\n近期拜访情况:\n${recentCalls}`;
          }
        }
      } catch (err) {
        console.warn('Failed to fetch context', err);
      }

      const inputContent = this.buildFeedbackPrompt(rawContent, contextInfo);

      let accumulatedResult = '';
      let buffer = '';

      streamFeedbackSummary({
        data: { content: inputContent },
        onChunk: (chunk) => {
          if (chunk) {
            buffer += chunk;
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith('data:')) continue;
              const jsonStr = trimmed.substring(5).trim();
              if (!jsonStr) continue;

              try {
                const json = JSON.parse(jsonStr);
                if (json.output && typeof json.output.text === 'string') {
                  const newText = json.output.text;
                  // Handle incremental vs full text
                  if (accumulatedResult && newText.startsWith(accumulatedResult)) {
                    accumulatedResult = newText;
                  } else {
                    accumulatedResult += newText;
                  }

                  // Parse content
                  const parsed = this.parseAIResponse(accumulatedResult);

                  // Update the specific item in the list
                  if (this.insightFormList[currentItemIndex]) {
                    const updatedItem = {
                      ...this.insightFormList[currentItemIndex],
                      feedback_summary: accumulatedResult, // Save full result as requested
                      tags: parsed.tags
                    };
                    this.$set(this.insightFormList, currentItemIndex, updatedItem);

                    if (parsed.suggestions && parsed.suggestions.length) {
                      this.suggestion = this.$t('suggestion_prefix') + parsed.suggestions[0];
                    }
                  }
                }
              } catch (e) {
                // Ignore parse errors
              }
            }
          }
        },
        onComplete: (fullText) => {
          // Process remaining buffer
          if (buffer && buffer.trim().startsWith('data:')) {
            try {
              const jsonStr = buffer.trim().substring(5).trim();
              const json = JSON.parse(jsonStr);
              if (json.output && typeof json.output.text === 'string') {
                const newText = json.output.text;
                if (accumulatedResult && newText.startsWith(accumulatedResult)) {
                  accumulatedResult = newText;
                } else {
                  accumulatedResult += newText;
                }
              }
            } catch (e) {
              // Ignore parse errors
            }
          }

          const finalText = accumulatedResult; // Ignore raw fullText

          const parsed = this.parseAIResponse(finalText);

          if (parsed.suggestions.length > 0) {
            this.suggestion = this.$t('suggestion_prefix') + parsed.suggestions[0];
          }

          if (this.insightFormList[currentItemIndex]) {
            const updatedItem = {
              ...this.insightFormList[currentItemIndex],
              feedback_summary: finalText, // Save parsed result
              tags: parsed.tags,
              isAiLoading: false
            };
            this.$set(this.insightFormList, currentItemIndex, updatedItem);
          }
        },
        onError: (err) => {
          Toast.fail(this.$t('ai_summary_failed'));
          console.error(err);
          if (this.insightFormList[currentItemIndex]) {
            const updatedItem = {
              ...this.insightFormList[currentItemIndex],
              isAiLoading: false,
              feedback_summary: accumulatedResult || rawContent
            };
            this.$set(this.insightFormList, currentItemIndex, updatedItem);
          }
        }
      });
    },
    handleTranslateResult(result) {
      this.tempTranslateResult += result;
    },
    async handleRecordComplete({localId, translateResult, duration}) {
      if (this.autoSendingVoice) {
        return;
      }
      // Use tempTranslateResult if available (streaming), otherwise use final result
      const content = (this.tempTranslateResult || translateResult || '').trim();

      // this.inputText = content; // Show in input box
      this.tempTranslateResult = '';

      const pendingVoiceData = {
        localId,
        feedback_voice_source: null, // This will be the S3 URL eventually, but initially the serverId
        feedback_transcript: content,
        duration: duration || 0
      };
      this.pendingVoiceData = pendingVoiceData;

      if (!content) {
        Toast('语音识别失败，请重试');
        return;
      }

      try {
        const res = await wecomSDK.uploadVoice(localId);
        // WeCom serverId (media_id)
        const serverId = res.serverId;

        if (pendingVoiceData.localId === localId) {
          // Store serverId as temporary source, or separate field
          pendingVoiceData.feedback_voice_media_id = serverId;
          // feedback_voice_source might be kept null until S3 URL is ready,
          // but for saving to DB, we pass media_id in a specific column.
        }
      } catch (e) {
        console.error('Upload voice failed', e);
      }

      if (!pendingVoiceData.feedback_voice_media_id) {
        Toast('语音上传失败，请重试');
        return;
      }

      this.pendingVoiceData = {...pendingVoiceData};
      this.autoSendingVoice = true;
      try {
        await this.sendText();
      } finally {
        this.autoSendingVoice = false;
      }
    },
    async fetchAutoSummary(content) {
      // Placeholder for automatic summary interface
      // return request({
      //   url: '/api/summary',
      //   method: 'post',
      //   data: { content }
      // });
      return Promise.resolve({ tags: [] });
    },
    async save(source_msl_call_id, type, physician_id, products = []) {
      // 1. Process deletions
      if (this.deletedIds && this.deletedIds.length > 0) {
        await Promise.all(
          this.deletedIds.map(async (id) => {
            try {
              await deletePhysicianMrCallKeyFeedback(id);
            } catch (err) {
              console.error('Failed to delete feedback', id, err);
            }
          })
        );
        this.deletedIds = []; // Clear after deleting
      }

      // 2. Process creates/updates
      await Promise.all(
        this.insightFormList.map(async (item) => {
          const feedbackId = item.physician_mr_call_key_feedback_id;
          if (feedbackId && !item._isModified) return;
          if (feedbackId && !this.canManageFeedback(item)) return;

          const feedback_labels = Array.isArray(item.tags) ? item.tags.join(',') : '';

          let product = '';
          let product_id = '';
          if (products && products.length > 0) {
            product = products.map(p => p.product_name).join(',');
            product_id = products.map(p => p.product_id).join(',');
          }

          const rawData = {
            physician_id,
            feedback_time: new Date().toISOString(),
            physician_mr_call_id: source_msl_call_id,
            key_question_id: item.key_question_id,
            feedback_content: item.feedback_content,
            // Pass media_id and duration
            feedback_voice_media_id: item.feedback_voice_media_id, // WeCom media_id
            feedback_voice_seconds: item.duration, // Duration in seconds
            // feedback_voice_source might be empty initially, updated by server later
            feedback_voice_source: '',
            feedback_transcript: item.feedback_transcript || '', // Original text from voice
            feedback_labels,
            feedback_summary: item.feedback_summary, // Optional
            feedback_follow_up: '', // Optional
            product,
            product_id,
            ta: '',
            ta_id: ''
          };

          if (!this.isCurrentUserVisitOwner()) {
            Object.assign(rawData, this.getCurrentFeedbackAuthorFields());
          }

          // Remove null/undefined values to prevent OData errors
          const data = Object.fromEntries(
            Object.entries(rawData).filter(([_, value]) => value !== null && value !== undefined)
          );

          if (feedbackId) {
            // Update existing
            await updatePhysicianMrCallKeyFeedback(feedbackId, data);
          } else {
            // Create New
            const res = await createPhysicianMrCallKeyFeedback(data);

            const newId = res && res.entity ? res.entity.physician_mr_call_key_feedback_id : null;

            // If there is audio, trigger server-side download to S3
            if (newId && item.feedback_voice_media_id) {
              try {
                await syncVoiceToS3({
                  physician_mr_call_key_feedback_id: newId, // The ID of the feedback record
                  feedback_voice_media_id: item.feedback_voice_media_id
                });
              } catch (err) {
                console.error('Failed to sync voice to S3', err);
              }
            }
          }
        })
      );
      this.insightFormList = this.insightFormList.map((item) => ({
        ...item,
        _isModified: false,
      }));
      this.originalSnapshot = JSON.stringify(this.insightFormList);
      this.deletedIds = [];
      this.resetDraftBaseline();
      this.emitDraftChange();
    },
    addInsightForm(data) {
      // Compatibility if parent calls this
      this.insightFormList.push({
        ...data,
        ...this.getCurrentFeedbackAuthorFields(),
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.feedback-list-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.list-content {
  flex: 1;
  padding: 10px;
  overflow-y: auto;
}

.bottom-bar {
  background: transparent;
  margin-bottom: 80px;
//   padding: 10px 0 20px;
}

.suggestion {
  display: inline-flex;
  align-items: flex-start;
  font-size: 14px;
  line-height: 20px;
  color: #666;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  padding: 16px;
  margin-top: 4px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.input-area {
  margin: 0 -10px;
  display: flex;
  align-items: center;
}

.selected-question-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 4px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid #f2f5f9;
  width: 100%;

  .question-info {
    flex: 1;
    margin-right: 8px;
    display: flex;
    flex-direction: column;
  }

  .text {
    color: #aa341b;
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 4px;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .desc {
    color: #666;
    font-size: 13px;
    line-height: 1.3;
    white-space: pre-wrap;
  }

  .close-btn {
    color: #999;
    font-size: 16px;
    padding: 2px;
  }
}

.single-choice-box {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0 8px 0;
  width: 100%;

  .option-item {
    padding: 6px 14px;
    background-color: #f7f8fa;
    border: 1px solid #ebedf0;
    border-radius: 16px;
    font-size: 13px;
    color: #333;
    cursor: pointer;
    transition: all 0.2s ease;

    &.active {
      background-color: rgba(170, 52, 27, 0.1);
      border-color: #aa341b;
      color: #aa341b;
    }
  }
}

.input-row {
  width: 100%;
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 48px;
  padding: 12px 8px 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.add-question-btn {
  display: flex;
  align-items: center;
  color: #aa341b;
  font-size: 11px;
  font-weight: 900;
  margin-right: 10px;
  white-space: nowrap;

  .van-icon {
    font-size: 18px;
    margin-right: 2px;
  }
}

.input-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden; /* Prevent overflow */

  .text-input {
    width: 100%;
    padding: 0;
    background: transparent;
    font-size: 14px;

    ::v-deep .van-field__control {
      color: #333;
      max-height: 100px;
      &::placeholder {
        color: #ccc;
      }
    }
  }
}

.action-btn {
  margin-left: 0;
  padding-left: 8px;
  display: flex;
  align-items: center;
}

.mic-btn {
  width: 48px;
  height: 48px;
  background: #aa341b;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;

  .title {
    font-size: 16px;
    font-weight: bold;
  }
}

::v-deep .van-tree-select__item {
  white-space: normal;
  height: auto;
  line-height: normal;
  padding-top: 10px;
  padding-bottom: 10px;
}

::v-deep .van-tree-select__nav {
  flex: 0 0 34%;
  max-width: 34%;
}

::v-deep .van-tree-select__content {
  flex: 1;
  min-width: 0;
}

::v-deep .van-sidebar-item__text {
  white-space: normal;
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.custom-tree-content {
  padding: 10px;
  overflow-y: auto;
  height: 100%;
}

.custom-tree-item {
  padding: 12px;
  background: #f7f8fa;
  border-radius: 6px;
  margin-bottom: 10px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.custom-tree-item.active {
  background: rgba(170, 52, 27, 0.05);
  border-color: #aa341b;
}

.q-title {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin-bottom: 6px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.q-text {
  flex: 1;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.q-type {
  font-size: 10px;
  color: #fff;
  background: #aa341b;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
  white-space: nowrap;
  font-weight: normal;
}

.q-desc {
  font-size: 12px;
  color: #666;
  white-space: pre-wrap;
  line-height: 1.5;
  margin-top: 4px;
  word-break: break-word;
  overflow-wrap: anywhere;
}
</style>
